import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePrivy } from "@privy-io/react-auth";
import { useChallenge } from "@/hooks/useChallenge";
import { useChallengeContract } from "@/hooks";
import { SelectionButton } from "@/components/SelectionButton";
import { Calendar } from "@/components/Calendar";
import { Tooltip } from "@/components/Tooltip";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorToast } from "@/components/ErrorToast";
import { StickyHeader } from "@/components/ui/StickyHeader";
import { HeaderIconButton } from "@/components/ui/HeaderIconButton";
import { decodeEventLog } from "viem";
import DasherFactoryABI from "@/abi/DasherFactory";

/** Format date components to "YYYY-MM-DD" string */
function formatDateString(year: number, month: number, day: number): string {
  const m = String(month + 1).padStart(2, "0"); // month is 0-indexed
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

/** Get tomorrow's date as "YYYY-MM-DD" string */
function getTomorrowString(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return formatDateString(
    tomorrow.getFullYear(),
    tomorrow.getMonth(),
    tomorrow.getDate()
  );
}

/** Parse "YYYY-MM-DD" to display format (e.g., "Dec 11, 2024") */
function formatDateForDisplay(dateStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Calculate end date string given start date and duration */
function calculateEndDateString(
  startDateStr: string,
  durationDays: number
): string {
  const [year, month, day] = startDateStr.split("-").map(Number);
  const startDate = new Date(year, month - 1, day);
  startDate.setDate(startDate.getDate() + durationDays - 1); // -1 because start and end are inclusive
  return formatDateString(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
}

const CHALLENGE_TYPES = [
  { value: "running", label: "Running", icon: "directions_run" },
  { value: "marathon", label: "Marathon", icon: "emoji_events" },
];

const DURATIONS = [
  { value: 7, label: "7 Days" },
  { value: 14, label: "14 Days" },
  { value: 21, label: "21 Days" },
];

const STAKE_AMOUNTS = [
  { value: 5, label: "$5" },
  { value: 10, label: "$10" },
  { value: 25, label: "$25" },
  { value: 30, label: "$30" },
];

/** Marathon distance in kilometers */
const MARATHON_DISTANCE_KM = 42.195;

/**
 * Convert blockchain/RPC errors to user-friendly messages
 */
function parseErrorMessage(error: any): string {
  // Extract error message
  const errorMessage = error?.message || String(error);

  // Common error patterns and their user-friendly translations
  if (
    errorMessage.includes("User rejected") ||
    errorMessage.includes("user rejected")
  ) {
    return "You cancelled the transaction. Please try again when you're ready.";
  }

  if (
    errorMessage.includes("insufficient funds") ||
    errorMessage.includes("Insufficient funds")
  ) {
    return "Insufficient funds in your wallet to complete this transaction.";
  }

  if (
    errorMessage.includes("Policy ID(s) not found") ||
    errorMessage.includes("Sponsorship failed")
  ) {
    return "Gas sponsorship is temporarily unavailable. Please try again in a moment.";
  }

  if (
    errorMessage.includes("Network error") ||
    errorMessage.includes("network")
  ) {
    return "Network connection issue. Please check your internet and try again.";
  }

  if (
    errorMessage.includes("Transaction reverted") ||
    errorMessage.includes("reverted")
  ) {
    return "Transaction failed. This might be due to contract conditions not being met.";
  }

  if (errorMessage.includes("nonce") || errorMessage.includes("Nonce")) {
    return "Transaction conflict detected. Please try again.";
  }

  if (errorMessage.includes("gas") && errorMessage.includes("required")) {
    return "This transaction requires more resources than available. Please try again.";
  }

  if (errorMessage.includes("timeout") || errorMessage.includes("timed out")) {
    return "Transaction is taking longer than expected. Please check back in a moment.";
  }

  if (errorMessage.includes("Buffer is not defined")) {
    return "Browser compatibility issue detected. Please refresh the page and try again.";
  }

  // If no specific pattern matches, return a generic friendly message
  return "Failed to create challenge. Please try again or contact support if the issue persists.";
}

export function CreateChallenge() {
  const navigate = useNavigate();
  const { user } = usePrivy();
  const { createChallenge: createChallengeInDB, isLoading: isDBLoading } =
    useChallenge();
  const {
    createChallenge: createChallengeOnChain,
    isLoading: isContractLoading,
  } = useChallengeContract();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "running", // "running" or "marathon"
    startDate: getTomorrowString(), // Default to tomorrow as "YYYY-MM-DD" (contract requires future date)
    dailyGoal: "", // Daily distance goal in kilometers (not used for marathon)
    duration: 14, // Not used for marathon (fixed at 1 day)
    stake: 25,
    // Marathon-specific: target time in HH:MM:SS
    targetHours: 6,
    targetMinutes: 0,
    targetSeconds: 0,
  });

  const isMarathon = formData.type === "marathon";

  const handleSubmit = async () => {
    try {
      setError(null);

      // ===== VALIDATION =====

      // Validate challenge name (required, non-empty, reasonable length)
      const trimmedName = formData.name.trim();
      if (!trimmedName) {
        setError("Please enter a challenge name");
        return;
      }
      if (trimmedName.length < 3) {
        setError("Challenge name must be at least 3 characters");
        return;
      }
      if (trimmedName.length > 100) {
        setError("Challenge name must be less than 100 characters");
        return;
      }

      // Validate based on challenge type
      let dailyGoalNum: number;
      let durationDays: number;
      let targetDurationSeconds: number | undefined;

      if (isMarathon) {
        // Marathon: fixed distance, validate target time
        dailyGoalNum = MARATHON_DISTANCE_KM;
        durationDays = 1;
        targetDurationSeconds =
          formData.targetHours * 3600 +
          formData.targetMinutes * 60 +
          formData.targetSeconds;

        if (targetDurationSeconds < 3600) {
          setError("Marathon target time must be at least 1 hour");
          return;
        }
        if (targetDurationSeconds > 12 * 3600) {
          setError("Marathon target time cannot exceed 12 hours");
          return;
        }
      } else {
        // Running: validate daily goal and duration
        dailyGoalNum = Number(formData.dailyGoal);
        if (!formData.dailyGoal || isNaN(dailyGoalNum) || dailyGoalNum <= 0) {
          setError("Please enter a valid daily goal (must be greater than 0)");
          return;
        }
        if (dailyGoalNum > 1000) {
          setError("Daily goal seems unrealistic (maximum 1000 km)");
          return;
        }

        durationDays = formData.duration;
        if (durationDays % 7 !== 0) {
          setError("Challenge duration must be in weeks (7, 14, or 21 days)");
          return;
        }
      }

      // Validate start date (must be tomorrow or later - contract requires future date)
      const tomorrowStr = getTomorrowString();
      if (formData.startDate < tomorrowStr) {
        setToastMessage(
          "Challenges must start from tomorrow onwards. Please select a future date to give participants time to join!"
        );
        return;
      }

      // Validate user authentication and wallet
      if (!user) {
        setError("You must be logged in to create a challenge");
        return;
      }

      // Get wallet address from Privy user
      const walletAddress = user.wallet?.address;
      if (!walletAddress) {
        setError(
          "Wallet address not found. Please ensure you're logged in with a wallet."
        );
        return;
      }

      // Sanitize description (optional field)
      const trimmedDescription = formData.description.trim();
      if (trimmedDescription.length > 500) {
        setError("Description must be less than 500 characters");
        return;
      }

      // ===== CHALLENGE CREATION =====
      let challengeAddress: string;

      if (isMarathon) {
        // Marathon: Off-chain challenge - generate unique ID
        challengeAddress = `marathon-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      } else {
        // Running: On-chain challenge via smart contract

        // Convert date string to Unix timestamp (seconds) at UTC midnight
        const [startYear, startMonth, startDay] = formData.startDate
          .split("-")
          .map(Number);
        const startDateUTC = new Date(
          Date.UTC(startYear, startMonth - 1, startDay, 0, 0, 0, 0)
        );
        const startTimestamp = Math.floor(startDateUTC.getTime() / 1000);
        const durationInSeconds = durationDays * 24 * 60 * 60;

        // Call smart contract to create challenge (gasless)
        const txResult = await createChallengeOnChain({
          startDate: startTimestamp,
          duration: durationInSeconds,
          stakeAmount: formData.stake.toString(),
        });

        // Extract challenge address from transaction hash
        let extractedAddress: string | null = null;

        // Check for both hash formats (txnHash from Alchemy, hash from other providers)
        const txHash =
          txResult && typeof txResult === "object"
            ? (txResult as any).txnHash || (txResult as any).hash
            : null;

        if (txHash) {
          // Wait for transaction receipt to get event logs
          const { createPublicClient, http } = await import("viem");
          const { base } = await import("viem/chains");
          const { ALCHEMY_API_KEY } = await import("@/utils/constants");

          const publicClient = createPublicClient({
            chain: base,
            transport: http(
              `https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`
            ),
          });

          const receipt = await publicClient.waitForTransactionReceipt({
            hash: txHash as `0x${string}`,
          });

          // Parse logs to find ChallengeCreated event
          for (const log of receipt.logs) {
            try {
              const decodedLog = decodeEventLog({
                abi: DasherFactoryABI,
                data: log.data,
                topics: log.topics,
              });

              if (decodedLog.eventName === "ChallengeCreated") {
                extractedAddress = (decodedLog.args as any)
                  ._challengeContractAddress;
                break;
              }
            } catch (e) {
              // Skip logs that don't match our ABI
              continue;
            }
          }
        }

        if (!extractedAddress) {
          throw new Error(
            "Failed to get challenge address from transaction. Please try again."
          );
        }

        challengeAddress = extractedAddress;
      }

      // ===== DATABASE SAVE =====

      // Calculate total distance goal (dailyGoal × duration)
      const totalDistanceGoal = dailyGoalNum * durationDays;

      // Set required days = duration
      const requiredDays = durationDays;

      // Calculate end date string
      // For marathon (1 day): end date = start date
      // For running: startDate + duration - 1 (since both start and end are inclusive)
      const endDateStr = isMarathon
        ? formData.startDate
        : calculateEndDateString(formData.startDate, durationDays);

      // Map challenge type: "running" → "run" for backend enum
      const challengeType = formData.type === "running" ? "run" : formData.type;

      // Build backend payload
      const challengeData: any = {
        id: challengeAddress,
        creatorAddress: walletAddress,
        name: trimmedName,
        description: trimmedDescription || null,
        type: challengeType,
        dailyGoal: dailyGoalNum,
        totalDistanceGoal: totalDistanceGoal,
        durationDays: durationDays,
        requiredDays: requiredDays,
        stakeAmount: formData.stake,
        startDate: formData.startDate, // "YYYY-MM-DD" format
        endDate: endDateStr, // "YYYY-MM-DD" format
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Creator's local timezone
      };

      // Add marathon-specific fields
      if (isMarathon) {
        challengeData.targetDurationSeconds = targetDurationSeconds;
        challengeData.isOffChain = true;
      }

      // Create challenge via API
      await createChallengeInDB(challengeData);

      // Navigate to success page with challenge data
      navigate("/challenge-success", {
        state: {
          name: trimmedName,
          description: trimmedDescription,
          startDate: formData.startDate, // "YYYY-MM-DD" format
          endDate: endDateStr, // "YYYY-MM-DD" format
          dailyGoal: dailyGoalNum,
          totalGoal: totalDistanceGoal,
          duration: durationDays,
          stake: formData.stake,
          challengeAddress: challengeAddress,
          isMarathon: isMarathon,
          targetDurationSeconds: targetDurationSeconds,
        },
      });
    } catch (error) {
      console.error("[CreateChallenge] Failed to create challenge:", error);

      // Show user-friendly error message
      const friendlyMessage = parseErrorMessage(error);
      setError(friendlyMessage);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark">
      {/* Sticky Header with safe-area */}
      <StickyHeader
        transparent
        leftContent={
          <HeaderIconButton
            icon="arrow_back_ios_new"
            variant="subtle"
            onClick={() => navigate(-1)}
            ariaLabel="Go back"
          />
        }
        title="Create Challenge"
        className="border-b border-border-light dark:border-border-dark"
      />

      {/* Main Content */}
      <main className="p-4 pt-6 pb-32">
        {/* Screen reader only validation announcements */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {error && `Form validation error: ${error}`}
        </div>

        {/* Error Message */}
        {error && (
          <div
            role="alert"
            aria-live="assertive"
            className="mb-6 rounded-2xl border-2 border-red-500 bg-red-500/10 p-4"
          >
            <div className="flex items-start space-x-3">
              <span
                className="material-symbols-outlined text-red-500 flex-shrink-0 mt-0.5"
                aria-hidden="true"
              >
                error
              </span>
              <p className="text-sm font-semibold text-red-500 break-words flex-1">
                {error}
              </p>
            </div>
          </div>
        )}

        <form className="space-y-8">
          {/* Name & Description Section */}
          <div className="space-y-6">
            {/* Challenge Name */}
            <div className="relative">
              <input
                className="w-full border-2 border-border-light dark:border-border-dark bg-transparent px-5 py-4 text-lg font-semibold text-text-primary-light dark:text-text-primary-dark placeholder:text-text-secondary-light dark:placeholder:text-text-secondary-dark focus:border-primary focus:outline-none focus:ring-0 rounded-2xl"
                id="challenge-name"
                name="challenge-name"
                placeholder="Challenge Name"
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                aria-label="Challenge name"
                aria-required="true"
                aria-invalid={error && !formData.name.trim() ? "true" : "false"}
              />
              <label
                className="absolute -top-3 left-4 bg-background-light dark:bg-background-dark px-1 text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark"
                htmlFor="challenge-name"
              >
                Name
              </label>
            </div>

            {/* Description */}
            <div className="relative">
              <textarea
                className="w-full resize-none border-2 border-border-light dark:border-border-dark bg-transparent px-5 py-4 pb-8 text-text-primary-light dark:text-text-primary-dark placeholder:text-text-secondary-light dark:placeholder:text-text-secondary-dark focus:border-primary focus:outline-none focus:ring-0 rounded-2xl"
                id="description"
                name="description"
                placeholder="A few words about your challenge..."
                rows={2}
                maxLength={500}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                aria-label="Challenge description (optional)"
                aria-required="false"
              />
              <label
                className="absolute -top-3 left-4 bg-background-light dark:bg-background-dark px-1 text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark"
                htmlFor="description"
              >
                Description (Optional)
              </label>
              {/* Character Counter */}
              <div className="absolute bottom-2 right-4 text-xs font-medium">
                <span
                  className={
                    formData.description.length >= 490
                      ? "text-red-500"
                      : formData.description.length >= 450
                      ? "text-orange-500"
                      : "text-text-secondary-light dark:text-text-secondary-dark"
                  }
                >
                  {formData.description.length}/500
                </span>
              </div>
            </div>
          </div>

          {/* Type & Start Date Row */}
          <div className="grid grid-cols-2 gap-3">
            {/* Challenge Type Dropdown */}
            <div className="space-y-3">
              <label className="ml-1 block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                Type
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
                  className="flex h-16 w-full items-center justify-between rounded-2xl bg-card-light dark:bg-card-dark px-4 transition-all hover:bg-subtle-light dark:hover:bg-card-dark/80"
                  aria-expanded={isTypeDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <div className="flex items-center">
                    <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary/15">
                      <span className="material-symbols-outlined text-lg text-primary">
                        {CHALLENGE_TYPES.find((t) => t.value === formData.type)?.icon}
                      </span>
                    </div>
                    <span className="font-bold text-text-primary-light dark:text-text-primary-dark">
                      {CHALLENGE_TYPES.find((t) => t.value === formData.type)?.label}
                    </span>
                  </div>
                  <span
                    className={`material-symbols-outlined text-xl text-text-secondary-light dark:text-text-secondary-dark transition-transform duration-200 ${
                      isTypeDropdownOpen ? "rotate-180" : ""
                    }`}
                  >
                    expand_more
                  </span>
                </button>

                {/* Dropdown Menu */}
                {isTypeDropdownOpen && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsTypeDropdownOpen(false)}
                    />
                    {/* Menu */}
                    <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border border-border-light dark:border-border-dark bg-card-light dark:bg-card-dark shadow-lg">
                      {CHALLENGE_TYPES.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, type: type.value });
                            setIsTypeDropdownOpen(false);
                          }}
                          className={`flex w-full items-center px-4 py-3.5 transition-colors ${
                            formData.type === type.value
                              ? "bg-primary/10"
                              : "hover:bg-subtle-light dark:hover:bg-white/5"
                          }`}
                        >
                          <div
                            className={`mr-3 flex h-8 w-8 items-center justify-center rounded-full ${
                              formData.type === type.value
                                ? "bg-primary/20"
                                : "bg-primary/10"
                            }`}
                          >
                            <span
                              className={`material-symbols-outlined text-lg ${
                                formData.type === type.value
                                  ? "text-primary"
                                  : "text-text-secondary-light dark:text-text-secondary-dark"
                              }`}
                            >
                              {type.icon}
                            </span>
                          </div>
                          <span
                            className={`font-semibold ${
                              formData.type === type.value
                                ? "text-primary"
                                : "text-text-primary-light dark:text-text-primary-dark"
                            }`}
                          >
                            {type.label}
                          </span>
                          {formData.type === type.value && (
                            <span className="material-symbols-outlined ml-auto text-primary">
                              check
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Start Date */}
            <div className="space-y-3">
              <label className="ml-1 block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                Start Date
              </label>
              <button
                type="button"
                onClick={() => setIsCalendarOpen(true)}
                className="relative flex h-16 w-full items-center rounded-2xl bg-card-light dark:bg-card-dark px-4 hover:bg-subtle-light dark:hover:bg-card-dark/80 transition-all"
                aria-label={`Select start date. Currently selected: ${formatDateForDisplay(
                  formData.startDate
                )}`}
              >
                <span className="material-symbols-outlined mr-2 text-xl text-text-secondary-light dark:text-text-secondary-dark">
                  calendar_month
                </span>
                <span className="font-bold text-text-primary-light dark:text-text-primary-dark text-sm">
                  {formatDateForDisplay(formData.startDate)}
                </span>
              </button>
            </div>
          </div>

          {/* Conditional: Daily Goal (Running only) */}
          {!isMarathon && (
            <div className="space-y-3">
              <label
                className="ml-1 block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark"
                htmlFor="dailyGoal"
              >
                Daily Goal (km)
              </label>
              <div className="relative flex items-center rounded-2xl bg-card-light dark:bg-card-dark p-1.5">
                <span className="material-symbols-outlined absolute left-4 text-2xl text-text-secondary-light dark:text-text-secondary-dark">
                  straighten
                </span>
                <input
                  className="w-full border-none bg-transparent py-2.5 pl-12 pr-16 text-lg font-bold text-text-primary-light dark:text-text-primary-dark placeholder:text-text-secondary-light dark:placeholder:text-text-secondary-dark focus:outline-none focus:ring-0"
                  id="dailyGoal"
                  name="dailyGoal"
                  placeholder="e.g., 5"
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.dailyGoal}
                  onChange={(e) =>
                    setFormData({ ...formData, dailyGoal: e.target.value })
                  }
                  aria-label="Daily goal in kilometers"
                  aria-required="true"
                  aria-invalid={
                    error &&
                    (!formData.dailyGoal || Number(formData.dailyGoal) <= 0)
                      ? "true"
                      : "false"
                  }
                />
                <span className="absolute right-4 text-lg font-semibold text-text-secondary-light dark:text-text-secondary-dark">
                  km
                </span>
              </div>
            </div>
          )}

          {/* Conditional: Duration (Running only) */}
          {!isMarathon && (
            <div className="space-y-3">
              <label className="ml-1 block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                Duration
              </label>
              <div className="grid grid-cols-3 gap-2 rounded-2xl bg-card-light dark:bg-card-dark p-1.5">
                {DURATIONS.map((duration) => (
                  <SelectionButton
                    key={duration.value}
                    variant="segmented"
                    selected={formData.duration === duration.value}
                    onClick={() =>
                      setFormData({ ...formData, duration: duration.value })
                    }
                  >
                    {duration.label}
                  </SelectionButton>
                ))}
              </div>
            </div>
          )}

          {/* Conditional: Target Time (Marathon only) */}
          {isMarathon && (
            <div className="space-y-3">
              <div className="ml-1 flex items-center space-x-2">
                <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                  Target Completion Time
                </label>
                <Tooltip content="Participants must complete the full 42.195km marathon within this time to win.">
                  <span className="material-symbols-outlined text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    info
                  </span>
                </Tooltip>
              </div>
              <div className="flex items-center justify-center gap-2 rounded-2xl bg-card-light dark:bg-card-dark p-4">
                {/* Hours */}
                <div className="flex flex-col items-center gap-1">
                  <input
                    type="number"
                    min="0"
                    max="12"
                    value={formData.targetHours}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        targetHours: Math.max(0, Math.min(12, parseInt(e.target.value) || 0)),
                      })
                    }
                    onFocus={(e) => e.target.select()}
                    className="w-16 px-2 py-3 text-center text-xl font-bold rounded-xl border-2 border-border-light dark:border-border-dark bg-transparent text-text-primary-light dark:text-text-primary-dark focus:border-primary focus:outline-none"
                    aria-label="Hours"
                  />
                  <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark uppercase">
                    hrs
                  </span>
                </div>

                <span className="text-2xl font-bold text-text-secondary-light dark:text-text-secondary-dark pb-5">
                  :
                </span>

                {/* Minutes */}
                <div className="flex flex-col items-center gap-1">
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={formData.targetMinutes.toString().padStart(2, "0")}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        targetMinutes: Math.max(0, Math.min(59, parseInt(e.target.value) || 0)),
                      })
                    }
                    onFocus={(e) => e.target.select()}
                    className="w-16 px-2 py-3 text-center text-xl font-bold rounded-xl border-2 border-border-light dark:border-border-dark bg-transparent text-text-primary-light dark:text-text-primary-dark focus:border-primary focus:outline-none"
                    aria-label="Minutes"
                  />
                  <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark uppercase">
                    min
                  </span>
                </div>

                <span className="text-2xl font-bold text-text-secondary-light dark:text-text-secondary-dark pb-5">
                  :
                </span>

                {/* Seconds */}
                <div className="flex flex-col items-center gap-1">
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={formData.targetSeconds.toString().padStart(2, "0")}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        targetSeconds: Math.max(0, Math.min(59, parseInt(e.target.value) || 0)),
                      })
                    }
                    onFocus={(e) => e.target.select()}
                    className="w-16 px-2 py-3 text-center text-xl font-bold rounded-xl border-2 border-border-light dark:border-border-dark bg-transparent text-text-primary-light dark:text-text-primary-dark focus:border-primary focus:outline-none"
                    aria-label="Seconds"
                  />
                  <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark uppercase">
                    sec
                  </span>
                </div>
              </div>
              {/* Marathon info */}
              <p className="text-center text-sm text-text-secondary-light dark:text-text-secondary-dark">
                Distance: <span className="font-semibold">42.195 km</span> (Full Marathon)
              </p>
            </div>
          )}

          {/* Stake Amount */}
          <div className="space-y-3">
            <div className="ml-1 flex items-center space-x-2">
              <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                Stake Amount
              </label>
              <Tooltip
                content={
                  isMarathon
                    ? "Your stake will be held until the challenge ends. Winners share the prize pool."
                    : "Your stake will be locked in the smart contract until the challenge ends. Winners share the prize pool."
                }
              >
                <span className="material-symbols-outlined text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  info
                </span>
              </Tooltip>
            </div>
            <div className="grid grid-cols-4 gap-2 rounded-2xl bg-card-light dark:bg-card-dark p-1.5">
              {STAKE_AMOUNTS.map((amount) => (
                <SelectionButton
                  key={amount.value}
                  variant="segmented"
                  selected={formData.stake === amount.value}
                  onClick={() =>
                    setFormData({ ...formData, stake: amount.value })
                  }
                >
                  {amount.label}
                </SelectionButton>
              ))}
            </div>
          </div>
        </form>
      </main>

      {/* Fixed Bottom Button */}
      <footer className="fixed bottom-0 left-0 right-0 z-20 bg-background-light dark:bg-background-dark p-4 border-t border-border-light dark:border-border-dark">
        <button
          onClick={handleSubmit}
          disabled={isContractLoading || isDBLoading}
          className="flex w-full items-center justify-center rounded-4xl bg-primary py-4 text-lg font-extrabold text-black shadow-glow-primary transition-all hover:bg-opacity-90 active:scale-95 disabled:cursor-not-allowed disabled:bg-primary/90"
          type="submit"
          aria-label={
            isContractLoading || isDBLoading
              ? "Creating challenge, please wait"
              : "Create challenge"
          }
          aria-busy={isContractLoading || isDBLoading}
        >
          {isContractLoading || isDBLoading ? (
            <>
              <LoadingSpinner size="lg" className="mr-3" />
              <span>Creating...</span>
            </>
          ) : (
            <>
              <span>Create Challenge</span>
              <span className="material-symbols-outlined ml-2">
                arrow_forward
              </span>
            </>
          )}
        </button>
      </footer>

      {/* Calendar Modal */}
      {isCalendarOpen && (
        <Calendar
          selectedDate={formData.startDate}
          onSelect={(date) => setFormData({ ...formData, startDate: date })}
          onClose={() => setIsCalendarOpen(false)}
          minDate={getTomorrowString()}
        />
      )}

      {/* Toast for user-friendly messages */}
      <ErrorToast
        isOpen={!!toastMessage}
        onClose={() => setToastMessage(null)}
        message={toastMessage || ""}
        variant="warning"
        position="top"
      />
    </div>
  );
}
